/**
 * Twilio Migration API Schemas
 */

import { z } from "zod";

export const TwilioMigrationRecordSchema = z.object({
  id: z.string(),
  connectionId: z.string(),
  phoneNumber: z.string(),
  phoneNumberSid: z.string(),
  currentWebhookUrl: z.string(),
  expectedWebhookUrl: z.string(),
  webhookStatus: z.enum(["synced", "not_synced", "check_failed", "not_checked"]),
  ownership: z.enum(["pool", "customer", "unknown"]),
  isInSubaccount: z.boolean(),
  migratedAt: z.string().datetime().nullable(),
  error: z.string().nullable(),
  organizationName: z.string().nullable(),
  connectionName: z.string().nullable()
});

export const TwilioMigrationStatusResponseSchema = z.object({
  total: z.number(),
  webhookSynced: z.number(),
  webhookNotSynced: z.number(),
  webhookCheckFailed: z.number(),
  poolPhones: z.number(),
  customerPhones: z.number(),
  phones: z.array(TwilioMigrationRecordSchema),
  twilioChecked: z.boolean()
});

export const MigrateTwilioWebhookBodySchema = z.object({
  phoneId: z.string().uuid()
});

export const MigrateTwilioWebhookResponseSchema = z.object({
  success: z.boolean(),
  webhookUrl: z.string().optional(),
  usedMasterCredentials: z.boolean().optional(),
  error: z.string().optional()
});

export const VoiceMigrationRecordSchema = z.object({
  id: z.string(),
  connectionId: z.string(),
  phoneNumber: z.string(),
  phoneNumberSid: z.string(),
  currentWebhookUrl: z.string(),
  expectedWebhookUrl: z.string(),
  webhookStatus: z.enum(["synced", "not_synced", "check_failed", "not_checked"]),
  ownership: z.enum(["pool", "customer", "unknown"]),
  isInSubaccount: z.boolean(),
  migratedAt: z.string().datetime().nullable(),
  error: z.string().nullable(),
  organizationName: z.string().nullable(),
  connectionName: z.string().nullable()
});

export const VoiceMigrationStatusResponseSchema = z.object({
  total: z.number(),
  webhookSynced: z.number(),
  webhookNotSynced: z.number(),
  webhookCheckFailed: z.number(),
  poolPhones: z.number(),
  customerPhones: z.number(),
  phones: z.array(VoiceMigrationRecordSchema),
  twilioChecked: z.boolean()
});

export const MigrateVoiceWebhookBodySchema = z.object({
  phoneId: z.string().uuid()
});

export const MigrateVoiceWebhookResponseSchema = z.object({
  success: z.boolean(),
  webhookUrl: z.string().optional(),
  usedMasterCredentials: z.boolean().optional(),
  error: z.string().optional()
});

export const TransferPhoneBodySchema = z.object({
  phoneNumberId: z.string().uuid(),
  connectionId: z.string().uuid(),
  addressSid: z.string().optional()
});

/**
 * The transfer outcome as `TransferPhoneUseCase` actually returns it.
 *
 * This previously declared `phoneNumberSid` and `connectionId` — neither of
 * which the handler has ever sent — and omitted the three fields it always
 * does. Because the generated client `.parse()`s this schema, the correct
 * response would have been rejected on arrival; the endpoint was unusable
 * from `useTransfer` and only looked healthy because nothing calls it yet.
 * `connectionId` is a request field and stays one; the audit row reads it
 * from the body.
 */
export const TransferPhoneResponseSchema = z.object({
  success: z.boolean(),
  phoneNumberId: z.string(),
  phoneNumber: z.string(),
  fromAccount: z.string(),
  toAccount: z.string(),
  error: z.string().optional()
});
